import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Gift, Calendar, ArrowRight, Star, Tag, Zap, Info, Building2, BanknoteIcon, CreditCard } from 'lucide-react';

interface Notification {
  id: string;
  type: 'campaign' | 'news' | 'alert';
  title: string;
  description: string;
  bank?: {
    name: string;
    logo: string;
  };
  date: string;
  isNew: boolean;
  category: string;
  link?: string;
}

const notifications: Notification[] = [
  {
    id: '1',
    type: 'campaign',
    title: 'Yapı Kredi\'den Yeni Müşterilere Özel Fırsat!',
    description: 'İlk kez Yapı Kredi müşterisi olanlara özel %0 faizli 10.000 TL\'ye varan ihtiyaç kredisi fırsatı.',
    bank: {
      name: 'Yapı Kredi',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/52f6626d-498d-4e76-8fb5-fd4381a1e0db.png'
    },
    date: '2024-01-23',
    isNew: true,
    category: 'Kredi',
    link: '/ihtiyac-kredisi'
  },
  {
    id: '2',
    type: 'campaign',
    title: 'QNB Finansbank\'tan Hoş Geldin Mevduatı',
    description: '%45 faiz oranıyla 32 gün vadeli mevduat hesabı açın, yüksek kazanç elde edin.',
    bank: {
      name: 'QNB Finansbank',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/d9ae3539-f63d-4295-84e6-08dbe6e2a6d6.png'
    },
    date: '2024-01-22',
    isNew: true,
    category: 'Mevduat',
    link: '/mevduat'
  },
  {
    id: '3',
    type: 'news',
    title: 'Kredi Faiz Oranları Güncellendi',
    description: 'Tüm bankaların güncel kredi faiz oranları sistemimize yansıtılmıştır. En uygun krediyi bulmak için hemen karşılaştırın.',
    date: '2024-01-21',
    isNew: false,
    category: 'Genel',
    link: '/ihtiyac-kredisi'
  },
  {
    id: '4',
    type: 'campaign',
    title: 'Garanti BBVA\'dan Mil Puanlı Kart',
    description: 'Miles&Smiles kredi kartına başvurun, 5000 mil puan hediye kazanın!',
    bank: {
      name: 'Garanti BBVA',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2c60a02b-d37c-48ce-ad8e-deb4e57bf810.jpg'
    },
    date: '2024-01-20',
    isNew: false,
    category: 'Kredi Kartı',
    link: '/kredi-kartlari'
  }
];

const Bildirimler = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = [
    { id: 'all', name: 'Tümü', icon: Bell },
    { id: 'Kredi', name: 'Kredi', icon: BanknoteIcon },
    { id: 'Kredi Kartı', name: 'Kredi Kartı', icon: CreditCard },
    { id: 'Mevduat', name: 'Mevduat', icon: Building2 }
  ];

  const filteredNotifications = selectedCategory === null || selectedCategory === 'all'
    ? notifications
    : notifications.filter(n => n.category === selectedCategory);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'campaign':
        return <Gift className="text-[#F7BA45]" />;
      case 'news':
        return <Info className="text-blue-500" />;
      case 'alert':
        return <Bell className="text-red-500" />;
      default:
        return <Bell className="text-slate-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-gradient-to-b from-[#F7BA45]/20 to-slate-50 py-8 md:py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Bildirimler
            </h1>
            <p className="text-slate-600">
              En güncel banka kampanyaları ve fırsatlardan haberdar olun
            </p>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2 mt-8">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                      selectedCategory === category.id || (category.id === 'all' && selectedCategory === null)
                        ? 'bg-[#F7BA45] text-white shadow-md scale-105'
                        : 'bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Icon size={18} />
                    <span>{category.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-[#F7BA45]/50 transition-all duration-300 hover:shadow-lg"
              >
                <div className="p-4 md:p-6">
                  {/* Notification Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {notification.bank ? (
                        <img 
                          src={notification.bank.logo} 
                          alt={notification.bank.name} 
                          className="h-8 md:h-10 object-contain"
                        />
                      ) : (
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          {getNotificationIcon(notification.type)}
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-slate-800">{notification.title}</h3>
                          {notification.isNew && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F7BA45]/10 text-[#F7BA45] text-xs font-medium">
                              <Zap size={12} />
                              Yeni
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                          <Calendar size={14} />
                          <span>{new Date(notification.date).toLocaleDateString('tr-TR')}</span>
                          {notification.category && (
                            <>
                              <span>•</span>
                              <div className="flex items-center gap-1">
                                <Tag size={14} />
                                <span>{notification.category}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Notification Content */}
                  <p className="text-slate-600 mb-4">
                    {notification.description}
                  </p>

                  {/* Action Button */}
                  {notification.link && (
                    <div className="flex justify-end">
                      <Link 
                        to={notification.link}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 rounded-lg transition-all duration-300"
                      >
                        <span>Detaylı Bilgi</span>
                        <ArrowRight size={18} />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Bildirimler;